import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { useContact } from '../context/ContactContext';
import './Contact.css';

function Contact() {
  const { contactSettings } = useContact();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for contacting us! We\'ll get back to you within 24 hours.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1>Contact Us</h1>
          <p>We're here to help you plan your perfect trip</p>
        </div>
      </div>

      <div className="container">
        <div className="contact-content">
          <div className="contact-info-section">
            <h2>Get in Touch</h2>
            <p className="contact-intro">
              Have questions? Need assistance with your booking? Our dedicated team is available 24/7 
              to help you with all your travel needs.
            </p>

            <div className="contact-cards">
              <div className="contact-card">
                <div className="card-icon">
                  <Phone size={32} />
                </div>
                <h3>Call Us</h3>
                <p className="card-description">Speak with our travel experts</p>
                <a href={`tel:${contactSettings.tfn.replace(/[^0-9+]/g, '')}`} className="contact-link-main">
                  {contactSettings.tfn}
                </a>
                <p className="card-availability">{contactSettings.workingHours || 'Available 24/7'}</p>
              </div>

              <div className="contact-card">
                <div className="card-icon">
                  <Mail size={32} />
                </div>
                <h3>Email Us</h3>
                <p className="card-description">Send us your questions</p>
                <a href={`mailto:${contactSettings.email}`} className="contact-link-main">
                  {contactSettings.email}
                </a>
                <p className="card-availability">Response within 2-4 hours</p>
              </div>

              <div className="contact-card">
                <div className="card-icon">
                  <MapPin size={32} />
                </div>
                <h3>Visit Us</h3>
                <p className="card-description">Our office location</p>
                <address className="contact-address">
                  1309 Coffeen Ave<br />
                  STE 1200<br />
                  Sheridan, WY 82801<br />
                  USA
                </address>
              </div>

              <div className="contact-card">
                <div className="card-icon">
                  <Clock size={32} />
                </div>
                <h3>Business Hours</h3>
                <p className="card-description">We're always available</p>
                <p className="contact-link-main">24/7/365</p>
                <p className="card-availability">Even on holidays!</p>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            <div className="form-header">
              <MessageCircle size={40} />
              <h2>Send Us a Message</h2>
              <p>Fill out the form below and we'll respond as soon as possible</p>
            </div>

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Inquiry</option>
                    <option value="booking">Booking Assistance</option>
                    <option value="cancellation">Cancellation/Refund</option>
                    <option value="change">Change Booking</option>
                    <option value="payment">Payment Issue</option>
                    <option value="complaint">Complaint</option>
                    <option value="feedback">Feedback</option>
                    <option value="partnership">Partnership Opportunity</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Tell us how we can help you..."
                ></textarea>
              </div>

              <button type="submit" className="submit-button">
                <Send size={20} />
                Send Message
              </button>
            </form>
          </div>

          <div className="map-section">
            <h2>Find Us on the Map</h2>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2847.8537168086866!2d-106.95926492346!3d44.797189871070836!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5335fabc2a124b95%3A0x679e116d27278aa9!2s1309%20Coffeen%20Ave%20STE%201200%2C%20Sheridan%2C%20WY%2082801%2C%20USA!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="450"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="FlyTravelDeals.us Office Location"
              ></iframe>
            </div>
          </div>

          <div className="emergency-contact">
            <h3>Need Immediate Assistance?</h3>
            <p>If you're experiencing a travel emergency or need urgent help, please call us immediately:</p>
            <a href="tel:+18444800252" className="emergency-button">
              <Phone size={24} />
              Call +1-888-859-0441 Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
